import { addDays } from 'date-fns';
import _ from 'lodash';
import { Info, Paperclip } from 'lucide-react';
import { ApplicationStatus } from 'modules/applications/domain/enums/application-status';
import applicationsResource from 'modules/applications/domain/resources/applications.resource';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { mapPlayerToAnnounced } from 'modules/teams/domain/data-mappers/announced-teams.mapper';
import { upsertChampionshipApplicationUsers } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  updatingChampionshipTeamApplicationSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useTeamById } from 'modules/teams/ui/hooks/use-team-by-id';
import { championshipSettingsSelector } from 'modules/tournaments/store/championship/selectors';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useMultiSelect } from 'shared/hooks';
import { AppDispatch } from 'store/store';
import EmptyButton from 'ud-ui/components/empty-button';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import { PlayerApplication } from '../../../domain/interfaces/player-application';
import useModalEditApplication from '../../hooks/use-modal-edit-application';
import ChampionshipApplicationFilters, {
  ChampionshipApplicationFiltersOption,
  ChampionshipTeamsFilterValues,
} from '../championship-application-filters/championship-application-filters';
import CreateTeamPlayerForm from './create-team-player-form';
import { BodyBlock, HeadBlock } from './styles';
import TeamPlayerCard from './team-player-card';

const statusItems: ChampionshipApplicationFiltersOption<ApplicationStatus>[] = [
  {
    status: ApplicationStatus.PENDING,
    color: 'black',
  },
  {
    status: ApplicationStatus.ACCEPTED,
    color: '#58B879',
  },
  {
    status: ApplicationStatus.DECLINED,
    color: '#FF7061',
  },
];

type ChampionshipTeamPlayersProps = {
  teamId: number;
  championshipId: number;
};

const ChampionshipTeamPlayers = ({ teamId, championshipId }: ChampionshipTeamPlayersProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { open, Modal } = useModalEditApplication();
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const updatingApplicationId = useSelector(updatingChampionshipTeamApplicationSelector);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const announcedTeam = announcedTeams ? announcedTeams[teamId] : null;

  const [status, selectStatus] = useState<ApplicationStatus>(ApplicationStatus.PENDING);
  const [name, setName] = useState<string>('');

  const { isLoading, data: detailTeam } = useTeamById(teamId);

  const mergedPlayers = useMemo(() => {
    if (isLoading || !announcedTeam || !detailTeam?.players) {
      return [];
    }

    const notRegisteredApplications = mapPlayerToAnnounced(detailTeam.players, {
      teamId,
      championshipId,
      teamCreatedAt: detailTeam.createdAt,
    });

    return _.uniqBy([...Object.values(announcedTeam.applications || []), ...notRegisteredApplications], 'player.id');
  }, [isLoading, announcedTeam, detailTeam?.players, detailTeam?.createdAt, teamId, championshipId]);

  const playersList = useMemo(() => {
    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .filter((app) => status === app.status)
      .filter((app) => {
        const query = name.toLowerCase();
        const firstName = app.player.firstName.toLowerCase();
        const lastName = app.player.lastName.toLowerCase();

        return firstName.includes(query) || lastName.includes(query);
      });
  }, [mergedPlayers, status, name]);

  const {
    selectedIds: selectedPlayers,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseUp,
    handleSelect,
    setSelectedIds: setSelectedPlayers,
  } = useMultiSelect<PlayerApplication>({
    items: playersList,
    getId: (item: PlayerApplication) => item.player.id,
  });

  const countByStatus = useMemo(() => {
    const defaultValue = {
      [ApplicationStatus.PENDING]: 0,
      [ApplicationStatus.ACCEPTED]: 0,
      [ApplicationStatus.DECLINED]: 0,
    };

    return mergedPlayers
      .filter((app) => !app.player.isReal)
      .reduce<Record<string, number>>((result, item) => {
        const count = result[item.status] || 0;
        return {
          ...result,
          [item.status]: count + 1,
        };
      }, defaultValue);
  }, [mergedPlayers]);

  const handleFilterChange = useCallback((values: ChampionshipTeamsFilterValues<ApplicationStatus>) => {
    setName(values.name);
    selectStatus(values.status);
  }, []);

  const handlePlayersStatusChange = useCallback(
    async (status: ApplicationStatus.ACCEPTED | ApplicationStatus.DECLINED) => {
      if (!announcedTeam || selectedPlayers.length === 0) return;

      const applications = mergedPlayers.map((app) => ({
        userId: app.player.id,
        name: app.player.firstName,
        surname: app.player.lastName,
        middleName: app.player.middleName,
        birthDate: app.player.birthDate,
        position: app.position,
        number: app.number,
        state: selectedPlayers.includes(app.player.id) ? status : app.status,
      }));

      // Check max allowed players in application before accepting
      if (status === ApplicationStatus.ACCEPTED) {
        const max = settings?.applicationMaxPlayers ?? null;
        if (typeof max === 'number') {
          const acceptedCount = applications.filter((a) => a.state === ApplicationStatus.ACCEPTED).length;
          if (acceptedCount > max) {
            toast.error('Заявляемое кол-во игроков не соответствует заявочной компании. Сверьтесь с условиями турнира.');
            return;
          }
        }
      }

      setIsBulkUpdating(true);
      try {
        await dispatch(
          upsertChampionshipApplicationUsers({
            teamId,
            championshipId,
            applications,
          }),
        ).unwrap();

        StepperMainTool.setByPath('stepper:main:applicationTeam', true);
        toast.success('Игроки успешно обновлены');
        setSelectedPlayers([]);
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [
      dispatch,
      teamId,
      championshipId,
      announcedTeam,
      selectedPlayers,
      setSelectedPlayers,
      settings?.applicationMaxPlayers,
      mergedPlayers,
    ],
  );

  const handleCopyLink = useCallback(async () => {
    const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
    if (endDate) {
      const now = new Date();
      if (endDate < now) {
        toast.warning('Заявочная компания закончилась. Что бы получить ссылку продлите дату окончания приёма заявок.');
        return;
      }
    }

    const token = await applicationsResource.getToken(championshipId, teamId);
    const params = new URLSearchParams();
    params.append('token', token.token);
    const link = `${window.location.origin}${Pages.APPLICATION_LINK.INDEX}?${params.toString()}`;
    navigator.clipboard.writeText(link);
    const expiresAt = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : addDays(new Date(), 1);
    toast.success(
      `Ссылка скопирована и будет активна до ${expiresAt.toLocaleDateString()} ${expiresAt.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      })}`,
    );
  }, [championshipId, settings?.applicationEndDate, teamId]);

  const body = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex-center h-32 w-full">
          <ClipLoader />
        </div>
      );
    }

    if (playersList.length === 0) {
      return (
        <div className="flex-center my-5 h-full">
          <UDText type="body-text" className="text-surfaceRomanSilver30">
            Список пуст
          </UDText>
        </div>
      );
    }

    return (
      <>
        {playersList.map((app, index) => (
          <TeamPlayerCard
            key={app.player.id}
            index={index + 1}
            teamId={app.teamId}
            player={app}
            isUpdating={updatingApplicationId === app.id || isBulkUpdating}
            isSelected={selectedPlayers.includes(app.player.id)}
            onSelect={handleSelect}
            onMouseDownCheckbox={(event) => handleMouseDown(app.player.id, event)}
            onMouseEnterCheckbox={() => handleMouseEnter(app.player.id)}
            onMouseLeaveCheckbox={() => handleMouseLeave(app.player.id)}
            onClick={() => open(app, teamId)}
            applicationMaxPlayers={settings?.applicationMaxPlayers ?? null}
            applications={announcedTeam?.applications}
          />
        ))}
      </>
    );
  }, [
    announcedTeam?.applications,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isBulkUpdating,
    isLoading,
    open,
    playersList,
    selectedPlayers,
    settings?.applicationMaxPlayers,
    teamId,
    updatingApplicationId,
  ]);

  if (!announcedTeam) {
    return null;
  }

  const startDate = settings?.applicationStartDate ? new Date(settings.applicationStartDate) : undefined;
  const endDate = settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined;
  const now = new Date();
  const isAfterStartDate = startDate ? startDate < now : true;
  const isBeforeEndDate = endDate ? endDate > now : true;

  const isRangeValid = isAfterStartDate && isBeforeEndDate;

  const playerIds = announcedTeam.applications.map((app) => app.player.id);

  return (
    <div className="mt-4 w-full px-6" onMouseUp={handleMouseUp}>
      <HeadBlock>
        <div className={'flex justify-between'}>
          <div className={'flex items-center'} style={{ gap: 17 }}>
            <TeamLogo size={60} url={announcedTeam.logo?.url} />
            <UDText type={'subhead'}>{announcedTeam.name}</UDText>
          </div>
          <div className="flex items-center" style={{ gap: 10 }}>
            <EmptyButton
              icon={<Paperclip size={16} />}
              color={'#007AFF'}
              style={{ gap: 6 }}
              onClick={handleCopyLink}
              disabled={!isRangeValid}
            >
              <UDText type={'subhead'} style={{ color: 'inherit' }}>
                Скопировать ссылку для заявки
              </UDText>
            </EmptyButton>
            {!isRangeValid && (
              <UDTippy
                interactive
                content={
                  <UDText type="subhead">
                    {!isAfterStartDate && (
                      <div>
                        <Link
                          className="text-LightBlue"
                          to={Pages.TOURNEYS.builders.edit(championshipId, 'rules')}
                          state={{ highlight: { applicationStartDate: true } }}
                        >
                          Заявочная компания
                        </Link>{' '}
                        ещё не началась. Дата начала {startDate?.toLocaleDateString()}. Для копирования ссылки измените дату начала
                        приёма заявок.
                      </div>
                    )}
                    {!isBeforeEndDate && (
                      <div>
                        <Link
                          className="text-LightBlue"
                          to={Pages.TOURNEYS.builders.edit(championshipId, 'rules')}
                          state={{ highlight: { applicationEndDate: true } }}
                        >
                          Заявочная компания
                        </Link>{' '}
                        закончилась. Дата окончания {endDate?.toLocaleDateString()}. Для копирования ссылки продлите дату окончания
                        приёма заявок.
                      </div>
                    )}
                  </UDText>
                }
              >
                <div>
                  <UDText type="subhead">⚠️</UDText>
                </div>
              </UDTippy>
            )}
            <UDTippy
              interactive
              content={
                <UDText type="subhead">
                  Ссылка для заявки будет скопирована в буфер обмена. Отправьте её в чат, чтобы игроки могли подать заявку на участие в
                  турнире.
                  <br />
                  ⚠️Срок ссылки до даты окончания приёма заявок в{' '}
                  <Link
                    className="text-LightBlue"
                    to={Pages.TOURNEYS.builders.edit(championshipId, 'rules')}
                    state={{ highlight: { applicationEndDate: true } }}
                  >
                    заявочной компании
                  </Link>{' '}
                  или 24 часа с момента создания ссылки, если дата не указана.
                </UDText>
              }
            >
              <Info size={16} />
            </UDTippy>
          </div>
        </div>
        <CreateTeamPlayerForm teamId={teamId} championshipId={championshipId} existPlayerIds={playerIds} />
      </HeadBlock>
      <BodyBlock>
        <div style={{ paddingInline: 20 }}>
          <ChampionshipApplicationFilters<ApplicationStatus>
            selectedStatus={status}
            name={name}
            options={statusItems}
            inputPlaceholder="ФИО игрока"
            countByStatus={countByStatus}
            onChange={handleFilterChange}
            selectedIds={selectedPlayers}
            onAccept={async () => await handlePlayersStatusChange(ApplicationStatus.ACCEPTED)}
            onDecline={async () => await handlePlayersStatusChange(ApplicationStatus.DECLINED)}
          />
        </div>
        <div
          style={{
            marginTop: 10,
            paddingInline: 20,
          }}
        >
          {body}
        </div>
      </BodyBlock>
      <Modal />
    </div>
  );
};

export default ChampionshipTeamPlayers;
